import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { createLoadingStarted } from "../../../filters/actions"
import { ACCOUNT_SELECTED, COOKIE_LOADED, REFETCH_DATA } from "../../../auth/actions"
import { waitForGlobalFilterSet } from "../../reports/customers/customersTable/epics"
import { fetchLoyaltyProgramRequested } from "../../../../api/actions"
import {
    LOYALTY_FORM_SUBMITTED,
    LOYALTY_PROGRAM_MOUNTED,
    LOYALTY_UNSUBSCRIBE_CLICKED,
    loyaltyProgramMounted,
} from "./actions"
import { isLoyaltyProgramAgendaOpened } from "../../../data/selectors"
import {
    LOYALTY_PROGRAM_CREATED,
    LOYALTY_PROGRAM_DELETED,
    LOYALTY_PROGRAM_UPDATED,
    loyaltyProgramCreated,
    loyaltyProgramDeleted,
    loyaltyProgramUpdated,
} from "../../../data/actions"
import { logger } from "../../../../utils/loggerWrap"
import { Observable, defer } from "rxjs"
import { Action, StateObject } from "../../../redux/types"
import { ofType, StateObservable } from "redux-observable"
import { catchError, delayWhen, filter, map, switchMap } from "rxjs/operators"
import type { LoyaltyProgramData } from "../../../data/types"
import { getHttpHeaders, getSelectedAccountId } from "../../../auth/selectors"
import { LoyaltyProgramAddedPayload, LoyaltyProgramDeletedPayload, LoyaltyProgramUpdatedPayload } from "../types"
import { constructAuditData } from "../../auditLog/utils/utils-payload-data"
import { createEventRequest, requestEvent } from "../../../../events/utils"
import { EventType } from "../../../../events/types"
import { EventDataResponse } from "../../../../graphql/types"
import { NotificationType, openNotification } from "../../../uiKit/Notification/Notification"

export const loyaltyProgramAgenaMountedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOYALTY_PROGRAM_MOUNTED, ACCOUNT_SELECTED, REFETCH_DATA, COOKIE_LOADED),
        delayWhen((action: Action) => waitForGlobalFilterSet(actions$, state$.value, action)),
        switchMap(() => {
            return [createLoadingStarted("loyalty-program"), fetchLoyaltyProgramRequested()]
        })
    )

export const loyaltyFormSubmittedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOYALTY_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("loyaltyFormSubmittedEpics - form submitted", {
                data: action.payload,
            })
            if (
                action.payload.oldValue?.rewardsIncentive === action.payload.newValue.rewardsIncentive &&
                action.payload.oldValue?.redemptionMinimum === action.payload.newValue.redemptionMinimum
            ) {
                return []
            } else if (!action.payload.oldValue) {
                return [
                    loyaltyProgramCreated({
                        id: deps.idgen.next(),
                        rewardsIncentive: action.payload.newValue.rewardsIncentive,
                        redemptionMinimum: action.payload.newValue.redemptionMinimum,
                        startDate: action.payload.newValue.startDate.tz("America/New_York").utc().format(),
                    }),
                ]
            } else {
                return [
                    loyaltyProgramUpdated({
                        ...action.payload.oldValue,
                        rewardsIncentive: action.payload.newValue.rewardsIncentive,
                        redemptionMinimum: action.payload.newValue.redemptionMinimum,
                    }),
                ]
            }
        })
    )

export const loyaltyFormUnsubscribedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOYALTY_UNSUBSCRIBE_CLICKED),
        switchMap((action: Action) => {
            if (action.payload.id) {
                return [loyaltyProgramDeleted(action.payload.id)]
            } else {
                return []
            }
        })
    )

export const fetchLoayaltyProgramOnAccountSwitch = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED, REFETCH_DATA),
        filter(() => isLoyaltyProgramAgendaOpened(state$.value)),
        map(() => loyaltyProgramMounted())
    )
// Loyalty Program

export const loyaltyProgramCreatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOYALTY_PROGRAM_CREATED),
        switchMap((action: Action) => {
            const loyalty: LoyaltyProgramData = action.payload.loyaltyProgram
            const accountId = getSelectedAccountId(deps.getState())

            const eventPayload: LoyaltyProgramAddedPayload = {
                id: loyalty.id,
                rewardsIncentive: loyalty.rewardsIncentive,
                redemptionMinimum: loyalty.redemptionMinimum,
                startDate: loyalty.startDate,
                auditData: constructAuditData(deps),
            }

            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.LoyaltyProgramAddRequested,
                        {
                            correlationId: deps.idgen.next(),
                            accountId: accountId as string,
                        },
                        eventPayload,
                        0
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.id
                    if (!id) {
                        throw new Error("loyaltyProgramCreatedEpic - Event ID is missing")
                    }

                    logger.info("loyaltyProgramCreatedEpic - event succeeded", { eventId: id })

                    return []
                }),
                catchError(err => {
                    logger.error("loyaltyProgramCreatedEpic - event failed", err)
                    openNotification(NotificationType.Error, "Loyalty Program Add Failed")
                    return []
                })
            )
        })
    )

export const loyaltyProgramUpdatedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOYALTY_PROGRAM_UPDATED),
        switchMap((action: Action) => {
            const loyalty: LoyaltyProgramData = action.payload.newValue
            const accountId = getSelectedAccountId(deps.getState())

            const eventPayload: LoyaltyProgramUpdatedPayload = {
                id: loyalty.id,
                rewardsIncentive: loyalty.rewardsIncentive ?? undefined,
                redemptionMinimum: loyalty.redemptionMinimum ?? undefined,
                startDate: loyalty.startDate ?? undefined,
                auditData: constructAuditData(deps),
            }

            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.LoyaltyProgramUpdateRequested,
                        {
                            correlationId: deps.idgen.next(),
                            accountId: accountId as string,
                        },
                        eventPayload,
                        0
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.id
                    if (!id) {
                        throw new Error("loyaltyProgramUpdatedEpic - Event ID is missing")
                    }

                    logger.info("loyaltyProgramUpdatedEpic - event succeeded", { eventId: id })
                    return []
                }),
                catchError(err => {
                    logger.error("loyaltyProgramUpdatedEpic - event failed", err)
                    openNotification(NotificationType.Error, "Loyalty Program Update Failed")
                    return []
                })
            )
        })
    )

export const loyaltyProgramDeletedEpic = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOYALTY_PROGRAM_DELETED),
        switchMap((action: Action) => {
            const accountId = getSelectedAccountId(deps.getState())

            const eventPayload: LoyaltyProgramDeletedPayload = {
                id: action.payload.id,
                auditData: constructAuditData(deps),
            }

            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.LoyaltyProgramDeleteRequested,
                        {
                            correlationId: deps.idgen.next(),
                            accountId: accountId as string,
                        },
                        eventPayload,
                        0
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.id
                    if (!id) {
                        throw new Error("loyaltyProgramDeletedEpic - Event ID is missing")
                    }

                    logger.info("loyaltyProgramDeletedEpic - event succeeded", { eventId: id })

                    return []
                }),
                catchError(err => {
                    logger.error("loyaltyProgramDeletedEpic - event failed", err)
                    openNotification(NotificationType.Error, "Loyalty Program Deleted Failed")
                    return []
                })
            )
        })
    )

export default [
    loyaltyFormUnsubscribedEpics,
    loyaltyFormSubmittedEpics,
    loyaltyProgramAgenaMountedEpics,
    fetchLoayaltyProgramOnAccountSwitch,
    loyaltyProgramCreatedEpic,
    loyaltyProgramUpdatedEpic,
    loyaltyProgramDeletedEpic,
]
